import { useNavigate } from "react-router";
import { toast } from "sonner";
import { VOUCHER_BALANCE_PATH } from "@/shared/configs/routes.config";
import { useJobContext } from "@/shared/contexts/JobContext";
import { Button } from "@/shared/ui/Button";
import { downloadFile } from "@/shared/utils/fileDownloader.utils";
import { JOB_RESOURCE } from "../../configs/constants";
import { getJob } from "../../services/jobs.service";
import { isInProgress, isSuccess, isCancelling } from "../../utils/job-status.utils";
import JobCancelConfirmation from "./JobCancelConfirmation";
import NavigateToVoucherCampaignsButton from "./NavigateToVoucherCampaignsButton";
import type { Job } from "../../types/jobs.types";

type JobModalButtonsProps = {
  job: Job
  onCloseModal: () => void
};

const getJobActionFlags = (job: Job) => {
  const { result } = job;

  return {
    isJobInProgress: isInProgress(job),
    isCancelling: isCancelling(job),
    isJobCancelable: !isInProgress(job) && job.deletable,
    hasDownloadFile: isSuccess(job) && !!result?.url && !result?.file_expired,
    hasErrorFile: !!result?.error_url,
    canNavigateToVoucherOrdersPage: isSuccess(job)
      && (job.resource === JOB_RESOURCE.ORDER_CREATION || job.resource === JOB_RESOURCE.CARD_REISSUANCE_BATCH),
    canNavigateToVoucherCampaigns: isSuccess(job) && job.resource === JOB_RESOURCE.VOUCHER_ATTRIBUTION,
    isNetBalanceStatementJob: job.resource === JOB_RESOURCE.NET_BALANCE_STATEMENT,
  };
};

const JobModalButtons = ({ job, onCloseModal }: JobModalButtonsProps) => {
  const navigate = useNavigate();
  const { autoNavigateJobUuid } = useJobContext();

  const { result } = job;
  const {
    isJobInProgress,
    isCancelling,
    isJobCancelable,
    hasDownloadFile,
    hasErrorFile,
    canNavigateToVoucherOrdersPage,
    canNavigateToVoucherCampaigns,
    isNetBalanceStatementJob,
  } = getJobActionFlags(job);

  const downloadResultFile = () => {
    if (!result?.url) return;
    const fileName = job.resource === JOB_RESOURCE.VOUCHER_INVOICES_EXPORT
      ? job.file_name || "factures.zip"
      : `resultat_${job.title.replace(/ /g, "_")}`;
    downloadFile(result?.url, fileName);
    toast.success("Votre fichier a été téléchargé avec succès !");
  };

  const downloadErrorFile = () => {
    if (!result?.error_url) return;
    const fileName = `erreurs_${job.title.replace(/ /g, "_")}`;
    downloadFile(result.error_url, fileName);
    toast.success("Le fichier d'erreurs a été téléchargé !");
  };

  const downloadNetBalanceStatement = async () => {
    try {
      // Si pas d'URL dans le résultat et que le job est terminé, essayer de rafraîchir
      if (!result?.url && isSuccess(job)) {
        try {
          const refreshedJob = await getJob(job.uuid);

          if (refreshedJob.data.job.result?.url) {
            const fileName = `etat_date_${job.title.replace(/ /g, "_")}.pdf`;
            downloadFile(refreshedJob.data.job.result.url, fileName);
            toast.success("État daté téléchargé avec succès !");
            return;
          }
        }
        catch (_refreshError) {
          // En cas d'erreur de rafraîchissement, continuer avec la logique de fallback
        }
      }

      // Utiliser l'URL du résultat si elle existe (c'est le cas avec Active Storage)
      if (result?.url) {
        const fileName = `etat_date_${job.title.replace(/ /g, "_")}.pdf`;
        downloadFile(result.url, fileName);
        toast.success("État daté téléchargé avec succès !");
        return;
      }

      // Si pas d'URL dans le résultat, informer l'utilisateur
      toast.info("L'état daté a été envoyé par e-mail. Vérifiez votre boîte de réception.");
    }
    catch (_error) {
      // En cas d'erreur, informer l'utilisateur
      toast.info("L'état daté a été envoyé par e-mail. Vérifiez votre boîte de réception.");
    }
  };

  const onNavigateToVoucherOrdersButtonClick = () => {
    const voucherOrderUrl = `${VOUCHER_BALANCE_PATH}/${job.result?.success?.[0]?.id ?? ""}`;
    navigate(voucherOrderUrl);
    onCloseModal();
  };

  return (
    <div className="w-full mt-4 px-6 flex items-end justify-between gap-2">
      {isJobCancelable && <JobCancelConfirmation job={job} onCancelled={onCloseModal} />}

      <div className="flex gap-2">
        {/* During a cancellation the gauge is winding down; "continue in background" is misleading,
            so it is hidden and the dialog's close (✕) is shown instead (see JobModal). */}
        {isJobInProgress && !isCancelling && <Button onClick={onCloseModal}>Continuer en arrière-plan</Button>}

        {canNavigateToVoucherCampaigns && (
          <NavigateToVoucherCampaignsButton
            onCloseModal={onCloseModal}
            autoNavigate={autoNavigateJobUuid === job.uuid}
          />
        )}

        {canNavigateToVoucherOrdersPage && (
          <Button
            variant="action"
            onClick={onNavigateToVoucherOrdersButtonClick}
          >
            Aller au bon de commande
          </Button>
        )}

        {hasDownloadFile && !isNetBalanceStatementJob && (
          <Button
            variant="outline"
            onClick={downloadResultFile}
          >
            {job.resource === JOB_RESOURCE.VOUCHER_INVOICES_EXPORT ? "Télécharger les factures (.zip)" : "Télécharger le fichier"}
          </Button>
        )}

        {isSuccess(job) && isNetBalanceStatementJob && (
          <Button
            variant="outline"
            onClick={downloadNetBalanceStatement}
          >
            Télécharger l'état daté
          </Button>
        )}

        {hasErrorFile && (
          <Button
            variant="outline"
            onClick={downloadErrorFile}
          >
            Télécharger les erreurs
          </Button>
        )}
      </div>
    </div>
  );
};

export default JobModalButtons;
